import React, { useState } from "react"
import { motion } from "framer-motion"
import { X, MessageSquare } from "lucide-react"
import InfoTooltip from "../../../../components/Tooltip/InfoTooltip"
import { formatNaira } from "../negotiation"

interface Props {
    customerName?: string
    pickup?: string
    destination?: string
    originalPrice: number
    currentPrice: number
    isCustomerCounter: boolean
    isSending: boolean
    // Resolves to a validation message to show, or null when the modal can close.
    onSubmit: (amount: number, message: string | null) => Promise<string | null>
    onClose: () => void
}

const MESSAGE_LIMIT = 1000

const CounterOrderModal: React.FC<Props> = ({
    customerName,
    pickup,
    destination,
    originalPrice,
    currentPrice,
    isCustomerCounter,
    isSending,
    onSubmit,
    onClose,
}) => {
    const [price, setPrice] = useState("")
    const [message, setMessage] = useState("")
    const [error, setError] = useState<string | null>(null)

    const suggestions = [1500, 3000, 5000].map((step) => currentPrice + step)

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        const parsed = Number(price.replace(/,/g, ""))
        if (!parsed || parsed <= 0) {
            setError("Enter a price above ₦0.")
            return
        }
        if (parsed === currentPrice) {
            setError("That matches the current price. Use Accept instead.")
            return
        }
        setError(null)
        const fieldError = await onSubmit(parsed, message.trim() || null)
        if (fieldError) setError(fieldError)
        else onClose()
    }

    return (
        <div
            className="fixed inset-0 z-[10020] flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
            onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
        >
            <motion.div
                initial={{ opacity: 0, scale: 0.93, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.93, y: 20 }}
                transition={{ duration: 0.2 }}
                className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
            >
                <div className="flex items-center justify-between p-5 border-b border-gray-100">
                    <div className="flex items-center gap-2">
                        <MessageSquare size={18} className="text-[#143887]" />
                        <h3 className="font-semibold text-[#00032A]">Send Counter Offer</h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                        type="button"
                        aria-label="Close"
                    >
                        <X size={16} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-5 space-y-4">
                    {/* Order summary */}
                    <div className="bg-gray-50 rounded-xl p-4">
                        <p className="text-xs text-gray-500">Customer</p>
                        <p className="font-semibold text-sm text-[#00032A] mt-0.5">{customerName ?? "Unknown customer"}</p>
                        <div className="mt-3 flex items-start justify-between gap-3">
                            <div>
                                <p className="text-xs text-gray-500">{isCustomerCounter ? "Their latest price" : "Their offer"}</p>
                                <p className="text-xl font-bold text-purple-600 mt-0.5">{formatNaira(currentPrice)}</p>
                                {isCustomerCounter && (
                                    <p className="text-[11px] text-gray-500 mt-0.5">Original price {formatNaira(originalPrice)}</p>
                                )}
                            </div>
                            {(pickup || destination) && (
                                <div className="text-right">
                                    <p className="text-xs text-gray-500">Route</p>
                                    <p className="text-xs font-medium text-[#00032A] mt-0.5 max-w-[160px] text-right leading-relaxed">
                                        {pickup ?? "—"} → {destination ?? "—"}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Counter price */}
                    <div>
                        <label htmlFor="counter-price" className="text-sm font-medium text-[#00032A] block mb-1.5">
                            Your Price <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 font-medium select-none">
                                ₦
                            </span>
                            <input
                                id="counter-price"
                                type="number"
                                inputMode="numeric"
                                value={price}
                                onChange={(e) => { setPrice(e.target.value); setError(null) }}
                                placeholder="Enter your price"
                                min={1}
                                autoFocus
                                className="w-full pl-8 pr-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-[#143887] focus:ring-1 focus:ring-[#143887] transition-colors"
                            />
                        </div>
                        <div className="mt-2 flex flex-wrap gap-1.5">
                            {suggestions.map((value) => (
                                <button
                                    key={value}
                                    type="button"
                                    onClick={() => { setPrice(String(value)); setError(null) }}
                                    className="rounded-full border border-gray-200 px-2.5 py-0.5 text-[11px] text-gray-600 hover:border-[#143887] hover:text-[#143887] transition-colors"
                                >
                                    {formatNaira(value)}
                                </button>
                            ))}
                        </div>
                        {error && <p className="mt-1.5 text-xs text-red-500">{error}</p>}
                    </div>

                    {/* Optional message */}
                    <div>
                        <label htmlFor="counter-message" className="text-sm font-medium text-[#00032A] mb-1.5 flex items-center gap-1.5">
                            Message <span className="font-normal text-gray-400">(optional)</span>
                            <InfoTooltip
                                position="right"
                                text="Shown to the customer in the mobile app next to your price."
                            />
                        </label>
                        <textarea
                            id="counter-message"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            maxLength={MESSAGE_LIMIT}
                            placeholder="e.g. Price covers fuel and the Third Mainland toll."
                            rows={3}
                            className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-[#143887] focus:ring-1 focus:ring-[#143887] resize-none transition-colors"
                        />
                        <p className="text-right text-[11px] text-gray-400">{message.length} / {MESSAGE_LIMIT}</p>
                    </div>

                    <div className="flex gap-3 pt-1">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 py-2.5 rounded-lg border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSending}
                            className="flex-1 py-2.5 rounded-lg bg-[#143887] hover:bg-[#0f2d6b] text-white text-sm font-semibold transition-colors disabled:opacity-50"
                        >
                            {isSending ? "Sending..." : "Send Counter Offer"}
                        </button>
                    </div>
                </form>
            </motion.div>
        </div>
    )
}

export default CounterOrderModal
